import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTheme } from "../components/ThemeContext";
import { Table, Button } from "react-bootstrap";
import {
  IoDocumentText,
  IoDocumentLock,
  IoDocumentAttach,
} from "react-icons/io5";
import { FaUserGroup } from "react-icons/fa6";
import NavMenu from "../components/NavMenu";
import axios from "axios";
import photo from "../img/no-data.svg";
import moment from "moment";
import { IoMdEye } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import CanvasJSReact from "@canvasjs/react-charts";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const safeLower = (v) => (v == null ? "" : String(v)).trim().toLowerCase();

const makeRowKey = (a) => {
  // Prefer stable unique ids
  const u = (a?.unique_doc_id || "").trim();
  if (u) return `doc:${u}`;

  const s = (a?.send_id || "").trim();
  if (s) return `send:${s}`;

  // Fallback composite (last resort)
  const staff = (a?.staff_id || "").toString().trim();
  const name = (a?.staff_name || "").trim();
  const app = (a?.appraisal_name || "").trim();
  const start = (a?.start_date || "").toString().trim();
  const due = (a?.due_date || "").toString().trim();

  return `mix:${staff}|${name}|${app}|${start}|${due}`;
};

const dedupeByKey = (arr) => {
  const map = new Map();
  for (const item of arr || []) {
    const k = makeRowKey(item);
    if (!map.has(k)) map.set(k, item);
  }
  return Array.from(map.values());
};

const getStatusStyle = (status) => {
  switch (safeLower(status)) {
    case "submitted":
      return { bg: "#e6ffe6", text: "#2e7d32", dot: "#4caf50" };
    case "employee signed":
      return { bg: "#d0f0fd", text: "#0077b6", dot: "#0096c7" };
    case "both signed":
      return { bg: "#f3e5f5", text: "#6a1b9a", dot: "#9c27b0" };
    case "reviewing":
      return { bg: "#fff3e0", text: "#ef6c00", dot: "#ff9800" };
    case "pending":
      return { bg: "#ffebee", text: "#c62828", dot: "#f44336" };
    default:
      return { bg: "#eceff1", text: "#546e7a", dot: "#90a4ae" };
  }
};

const HrDashboard = () => {
  const [appraisal, setAppraisal] = useState([]);
  const [selectedAppraisalName, setSelectedAppraisalName] = useState("");
  const [appraisalNames, setAppraisalNames] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    submitted: 0,
    pending: 0,
    reviewing: 0,
  });

  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchAppraisal = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No auth token found in localStorage");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getAllAppraisal`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data?.success) {
          const raw = Array.isArray(response.data.data) ? response.data.data : [];

          // ✅ Deduplicate to prevent duplicate rows
          const all = dedupeByKey(raw);

          // Summary counts
          const total = all.length;
          const submitted = all.filter((a) => safeLower(a.status) === "submitted")
            .length;
          const pending = all.filter((a) => safeLower(a.status) === "pending")
            .length;
          const reviewing = all.filter((a) => {
            const st = safeLower(a.status);
            return !st || st === "reviewing";
          }).length;

          setAppraisal(all);

          const uniqueNames = [...new Set(all.map((a) => a.appraisal_name).filter(Boolean))];
          setAppraisalNames(uniqueNames);

          setSummaryCounts({ total, submitted, pending, reviewing });
        } else {
          console.error(response.data?.message || "Failed to load appraisal data");
        }
      } catch (error) {
        console.error("Error fetching appraisal data:", error);
      }
    };

    fetchAppraisal();
  }, []);

  const statusCounts = useMemo(() => {
    return appraisal.reduce((acc, item) => {
      const status = safeLower(item.status) || "unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [appraisal]);

  const pieChartOptions = useMemo(() => {
    return {
      animationEnabled: true,
      backgroundColor: "transparent",
      legend: {
        verticalAlign: "bottom",
        horizontalAlign: "center",
        fontSize: 14,
        fontColor: isDark ? "#ffffff" : "#000000",
      },
      data: [
        {
          type: "pie",
          radius: "100%",
          showInLegend: true,
          legendText: "{label}",
          indexLabelFontSize: 12,
          indexLabel: "{label} - {y}",
          indexLabelFontColor: isDark ? "#ffffff" : "#000000",
          dataPoints: Object.entries(statusCounts).map(([status, count], index) => ({
            label: status.charAt(0).toUpperCase() + status.slice(1),
            y: count,
            color: isDark
              ? ["#ffb74d", "#4db6ac", "#9575cd", "#f06292", "#81c784", "#64b5f6"][
                  index % 6
                ]
              : undefined,
          })),
        },
      ],
    };
  }, [statusCounts, isDark]);

  const handleSubmit = useCallback(
    (role_id, send_id, unique_doc_id) => {
      navigate(`/submit-appraisal/${role_id}`, {
        state: { send_id, unique_doc_id },
      });
    },
    [navigate]
  );

  const renderTable = (data) => {
    // Extra safety: dedupe again before render (in case filters create duplicates)
    const uniqueData = dedupeByKey(data);

    const grouped = uniqueData.reduce((acc, emp) => {
      const dept = emp.department || "No Department";
      if (!acc[dept]) acc[dept] = [];
      acc[dept].push(emp);
      return acc;
    }, {});

    let rowNumber = 0;

    return (
      <Table
        bordered
        responsive
        hover
        size="sm"
        className={`mt-2 mb-0 ${isDark ? "table-dark" : ""}`}
        style={{ fontSize: "12px" }}
      >
        <thead>
          <tr className="text-center">
            <th>No</th>
            <th>Name</th>
            <th>Supervisor</th>
            <th>Start Date</th>
            <th>Due Date</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {uniqueData.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-4">
                <div className="d-flex flex-column align-items-center">
                  <img
                    src={photo}
                    alt="Illustration"
                    className="img-fluid mb-3"
                    width="100px"
                  />
                  <div className="text-secondary">No PA Form found</div>
                </div>
              </td>
            </tr>
          ) : (
            Object.entries(grouped).map(([dept, emps]) => (
              <React.Fragment key={dept}>
                <tr>
                  <td
                    colSpan="7"
                    className={`fw-bold text-start ${
                      isDark ? "bg-dark text-light" : "bg-light text-dark"
                    }`}
                  >
                    {dept}
                  </td>
                </tr>

                {emps.map((emp) => {
                  rowNumber += 1;
                  const { bg, text, dot } = getStatusStyle(emp.status || "");
                  return (
                    <tr
                      key={makeRowKey(emp)}
                      className="align-middle text-center"
                    >
                      <td>{rowNumber}</td>
                      <td className="text-start">{emp.staff_name}</td>
                      <td>{emp.supervisor_name}</td>
                      <td>
                        {emp.start_date
                          ? moment(emp.start_date).format("MMM-YYYY")
                          : "-"}
                      </td>
                      <td>
                        {emp.due_date
                          ? moment(emp.due_date).format("MMM-YYYY")
                          : "-"}
                      </td>

                      <td>
                        <div
                          className="d-inline-flex align-items-center px-2 py-1 rounded-pill"
                          style={{
                            backgroundColor: bg,
                            color: text,
                            fontWeight: 500,
                            textTransform: "capitalize",
                            fontSize: "12px",
                          }}
                        >
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              backgroundColor: dot,
                              borderRadius: "50%",
                              marginRight: "8px",
                            }}
                          />
                          {emp.status || "Unknown"}
                        </div>
                      </td>

                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          <Button
                            variant="outline-light"
                            size="sm"
                            style={{
                              color: isDark ? "#fff" : "#B31E8D",
                              borderColor: isDark ? "#fff" : "#B31E8D",
                            }}
                            onClick={() =>
                              handleSubmit(
                                emp.role_id,
                                emp.send_id,
                                emp.unique_doc_id
                              )
                            }
                          >
                            <IoMdEye size={20} />
                            &nbsp; View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </tbody>
      </Table>
    );
  };

  const submitted = useMemo(() => {
    return appraisal.filter((a) => {
      const isSubmitted = safeLower(a.status) === "submitted";
      const matchAppraisal = selectedAppraisalName
        ? a.appraisal_name === selectedAppraisalName
        : true;
      return isSubmitted && matchAppraisal;
    });
  }, [appraisal, selectedAppraisalName]);

  const notSubmitted = useMemo(() => {
    return appraisal.filter((a) => {
      const isNotSubmitted = safeLower(a.status) !== "submitted";
      const matchAppraisal = selectedAppraisalName
        ? a.appraisal_name === selectedAppraisalName
        : true;
      const matchStatus = selectedStatus
        ? safeLower(a.status) === safeLower(selectedStatus)
        : true;

      return isNotSubmitted && matchAppraisal && matchStatus;
    });
  }, [appraisal, selectedAppraisalName, selectedStatus]);

  const user = JSON.parse(localStorage.getItem("user"));
  const userDescription = user?.description || "";

  if (userDescription !== "HR") {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <h1>Unauthorized</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <>
      <NavMenu />
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="header-title fw-bold">Dashboard</h5>

          <select
            className={`form-select form-select-sm w-auto ${
              isDark ? "bg-dark text-light border-secondary" : ""
            }`}
            value={selectedAppraisalName}
            onChange={(e) => setSelectedAppraisalName(e.target.value)}
          >
            <option value="">All Appraisals</option>
            {appraisalNames.map((name, idx) => (
              <option key={idx} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="row g-4">
          <div className="col-md-6">
            <div
              className={`p-3 rounded shadow-sm d-flex flex-column fixed-height-card ${
                isDark ? "bg-dark text-light" : "bg-light text-dark"
              }`}
            >
              <h6 className="fw-semibold">Quick View</h6>

              <div className="d-flex flex-wrap gap-3 mt-3">
                <div
                  className={`rounded p-3 d-flex justify-content-between align-items-center ${
                    isDark
                      ? "bg-secondary bg-opacity-50 text-light"
                      : "bg-secondary bg-opacity-25 text-dark"
                  }`}
                  style={{ minWidth: "100px", flex: "1 1 45%", height: "100px" }}
                >
                  <div>
                    <div className="fs-3 fw-semibold mobile-text-title">
                      {summaryCounts.total}
                    </div>
                    <div className="text-secondary mobile-text">
                      Total Appraisal
                    </div>
                  </div>
                  <FaUserGroup
                    size={45}
                    color={isDark ? "#ffffff" : "#B31E8D"}
                  />
                </div>

                <div
                  className={`rounded p-3 d-flex justify-content-between align-items-center ${
                    isDark
                      ? "bg-secondary bg-opacity-50 text-light"
                      : "bg-secondary bg-opacity-25 text-dark"
                  }`}
                  style={{ minWidth: "100px", flex: "1 1 45%", height: "100px" }}
                >
                  <div>
                    <div className="fs-3 fw-semibold mobile-text-title">
                      {summaryCounts.submitted}
                    </div>
                    <div className="text-secondary mobile-text">Submitted</div>
                  </div>
                  <IoDocumentText
                    size={50}
                    color={isDark ? "#ffffff" : "#B31E8D"}
                  />
                </div>

                <div
                  className={`rounded p-3 d-flex justify-content-between align-items-center ${
                    isDark
                      ? "bg-secondary bg-opacity-50 text-light"
                      : "bg-secondary bg-opacity-25 text-dark"
                  }`}
                  style={{ minWidth: "100px", flex: "1 1 45%", height: "100px" }}
                >
                  <div>
                    <div className="fs-3 fw-semibold mobile-text-title">
                      {summaryCounts.pending}
                    </div>
                    <div className="text-secondary mobile-text">Pending</div>
                  </div>
                  <IoDocumentAttach
                    size={50}
                    color={isDark ? "#ffffff" : "#B31E8D"}
                  />
                </div>

                <div
                  className={`rounded p-3 d-flex justify-content-between align-items-center ${
                    isDark
                      ? "bg-secondary bg-opacity-50 text-light"
                      : "bg-secondary bg-opacity-25 text-dark"
                  }`}
                  style={{ minWidth: "100px", flex: "1 1 45%", height: "100px" }}
                >
                  <div>
                    <div className="fs-3 fw-semibold mobile-text-title">
                      {summaryCounts.reviewing}
                    </div>
                    <div className="text-secondary mobile-text">Reviewing</div>
                  </div>
                  <IoDocumentLock
                    size={50}
                    color={isDark ? "#ffffff" : "#B31E8D"}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div
              className={`p-3 rounded shadow-sm d-flex flex-column fixed-height-card ${
                isDark ? "bg-dark text-light" : "bg-light text-dark"
              }`}
            >
              <h6 className="fw-bold mb-0">Review Status Statistics</h6>

              <div className="flex-grow-1 d-flex justify-content-center align-items-center">
                {Object.keys(statusCounts).length > 0 ? (
                  <div
                    style={{
                      width: "100%",
                      height: "250px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CanvasJSChart options={pieChartOptions} />
                  </div>
                ) : (
                  <div className={isDark ? "text-light" : "text-muted"}>
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div
              className={`p-3 rounded shadow-sm d-flex flex-column fixed-height-card ${
                isDark ? "bg-dark text-light" : "bg-light text-dark"
              }`}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">Submitted Appraisal</h6>
              </div>
              {renderTable(submitted)}
            </div>
          </div>

          <div className="col-md-6">
            <div
              className={`p-3 rounded shadow-sm d-flex flex-column fixed-height-card ${
                isDark ? "bg-dark text-light" : "bg-light text-dark"
              }`}
            >
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                <h6 className="fw-bold mb-0">All Appraisal</h6>

                <div className="d-flex gap-2 ms-auto align-items-center">
                  <select
                    className={`form-select form-select-sm ${
                      isDark ? "bg-dark text-light border-secondary" : ""
                    }`}
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="employee signed">Employee Signed</option>
                    <option value="both signed">Both Signed</option>
                  </select>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSelectedAppraisalName("");
                      setSelectedStatus("");
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </div>

              {renderTable(notSubmitted)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HrDashboard;
